import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://kanguroo-crm-production.up.railway.app/api/v1';
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((c) => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const fmt = (n, cur = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n ?? 0);
const EMPTY = { customer_id: '', description: '', service_amount: '', media_budget_amount: '0', currency: 'TRY', billing_day: '1', notes: '' };

export default function Recurring() {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/finance/recurring/'), api.get('/customers/')])
      .then(([tplRes, custRes]) => { setTemplates(tplRes.data); setCustomers(custRes.data); })
      .catch(() => alert('Veri yuklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const custName = (id) => { const c = customers.find((x) => x.id === id); return c ? (c.company_name || c.full_name) : id?.slice(0, 8); };

  const handleSave = async () => {
    if (!form.customer_id) return alert('Musteri secin');
    if (!form.description) return alert('Aciklama zorunlu');
    if (!form.service_amount) return alert('Tutar zorunlu');
    setSaving(true);
    try {
      await api.post('/finance/recurring/', { ...form, service_amount: parseFloat(form.service_amount), media_budget_amount: parseFloat(form.media_budget_amount) || 0, billing_day: parseInt(form.billing_day) });
      alert('Sablon olusturuldu');
      setShowForm(false); setForm(EMPTY); load();
    } catch (e) { alert(e?.response?.data?.detail || 'Hata'); } finally { setSaving(false); }
  };

  const handleToggle = async (tpl) => {
    try { await api.put(`/finance/recurring/${tpl.id}`, { is_active: !tpl.is_active }); load(); }
    catch { alert('Guncelleme basarisiz'); }
  };

  const handleTrigger = async (id) => {
    if (!window.confirm('Bu sablondan simdi fatura olusturulsun mu?')) return;
    setTriggering(id);
    try {
      const res = await api.post(`/finance/recurring/${id}/trigger`);
      alert(`Fatura olusturuldu: ${res.data.invoice_number}`); load();
    } catch (e) { alert(e?.response?.data?.detail || 'Hata'); } finally { setTriggering(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sablonu silmek istediginizden emin misiniz?')) return;
    try { await api.delete(`/finance/recurring/${id}`); load(); } catch { alert('Silme basarisiz'); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tekrarlayan Faturalar</h1>
          <p className="text-sm text-gray-500 mt-1">Otomatik fatura sablonlari</p>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">+ Yeni Sablon</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Yeni Tekrarlayan Fatura Sablonu</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Musteri *</label>
                <select value={form.customer_id} onChange={(e) => set('customer_id', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Musteri secin</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.full_name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Aciklama *</label>
                <input type="text" value={form.description} onChange={(e) => set('description', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Aylik Sosyal Medya Yonetimi" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Hizmet Tutari *</label>
                <input type="number" value={form.service_amount} onChange={(e) => set('service_amount', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Medya Butcesi</label>
                <input type="number" value={form.media_budget_amount} onChange={(e) => set('media_budget_amount', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Para Birimi</label>
                <select value={form.currency} onChange={(e) => set('currency', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Fatura Gunu (1-28)</label>
                <input type="number" value={form.billing_day} onChange={(e) => set('billing_day', e.target.value)} min="1" max="28" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Notlar</label>
                <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowForm(false); setForm(EMPTY); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Iptal</button>
              <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition">{saving ? 'Kaydediliyor...' : 'Sablon Olustur'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white border border-gray-200 rounded-xl"><p className="text-4xl mb-2">🔄</p><p>Henuz sablon yok</p></div>
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <div key={tpl.id} className={`bg-white border rounded-xl p-5 flex items-center gap-4 hover:shadow-sm transition ${tpl.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">{custName(tpl.customer_id)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${tpl.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{tpl.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
                <p className="text-sm text-gray-600">{tpl.description}</p>
                <div className="flex gap-4 text-xs text-gray-400">
                  <span>Her ayin <strong className="text-gray-600">{tpl.billing_day}.</strong> gunu</span>
                  <span>Hizmet: <strong className="text-gray-600">{fmt(tpl.service_amount, tpl.currency)}</strong></span>
                  {parseFloat(tpl.media_budget_amount) > 0 && <span>Medya: <strong className="text-gray-600">{fmt(tpl.media_budget_amount, tpl.currency)}</strong></span>}
                  {tpl.last_triggered_at && <span>Son: <strong className="text-gray-600">{new Date(tpl.last_triggered_at).toLocaleDateString('tr-TR')}</strong></span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => handleTrigger(tpl.id)} disabled={triggering === tpl.id || !tpl.is_active} className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-40 transition">{triggering === tpl.id ? '...' : 'Simdi Olustur'}</button>
                <button onClick={() => handleToggle(tpl)} className={`border px-3 py-1.5 rounded-lg text-xs font-medium transition ${tpl.is_active ? 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}>{tpl.is_active ? 'Durdur' : 'Aktifles'}</button>
                <button onClick={() => handleDelete(tpl.id)} className="bg-red-50 text-red-500 border border-red-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-100 transition">Sil</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
